import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../context/AuthContext";
import AuctionCard from "../components/AuctionCard";
import Seo from "../components/Seo";
import { ArrowRight, ShieldCheck, Gavel, Search } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const HERO_IMG = "https://images.pexels.com/photos/17483087/pexels-photo-17483087.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Home() {
  const { t } = useLanguage();
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/auctions/featured`).then(r => setFeatured(r.data)).catch(() => {});
    axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <Seo
        title="Aste Online Curate — Orologi, Arte, Gioielli, Auto Classiche"
        description="Rarium: aste online curate in Italia per collezionisti. Aste di orologi vintage, arte, gioielli, auto classiche, vini pregiati, monete antiche e libri rari. Ogni lotto autenticato dai nostri esperti indipendenti."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Categorie Rarium",
          "itemListElement": categories.map((c, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": c.name,
            "url": `https://rariumgroup.it/category/${c.slug}`,
          })),
        }}
      />
      {/* HERO — asymmetric editorial bento */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10 lg:pt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-7 flex flex-col justify-between">
            <div className="overline text-stone-500">{t("Lotto in copertina — N° 001")}</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mt-6 fade-up">
              {t("Aste online curate.")} <em className="text-[#8C3A3A] not-italic">{t("Rarità")}</em>{t(" che meritano un pubblico.")}
            </h1>
            <p className="mt-6 text-stone-600 max-w-xl leading-relaxed">
              {t("Rarium è la casa d'aste online in Italia per collezionisti esigenti — aste di orologi, arte, gioielli, auto classiche, vini, monete e libri rari verificati da esperti indipendenti.")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/browse" data-testid="hero-browse-btn" className="text-xs uppercase tracking-widest font-semibold bg-[#8C3A3A] hover:bg-[#7F3434] text-stone-50 px-8 py-4 transition-colors inline-flex items-center gap-2">
                {t("Esplora le aste")} <ArrowRight size={14} />
              </Link>
              <Link to="/sell" data-testid="hero-sell-btn" className="text-xs uppercase tracking-widest font-semibold border border-stone-900 hover:bg-stone-900 hover:text-stone-50 px-8 py-4 transition-colors">
                {t("Vendi con noi")}
              </Link>
            </div>
            <div className="mt-16 grid grid-cols-3 gap-6 border-t border-stone-200 pt-6">
              <Stat k="12k+" v={t("Collezionisti")} />
              <Stat k="180+" v={t("Esperti verificati")} />
              <Stat k="4.8" v={t("Rating collezionisti")} />
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="zoom-wrap aspect-[4/5] lg:aspect-auto lg:h-full bg-stone-100">
              <img src={HERO_IMG} alt="Featured auction" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIE — horizontal scroll mobile, grid desktop */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-24">
        <div className="flex items-end justify-between">
          <div>
            <div className="overline text-stone-500">{t("Categorie")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-2">{t("Cosa cerchi oggi?")}</h2>
          </div>
          <Link to="/categories" className="hidden md:block underline-hover text-sm">{t("Vedi tutte →")}</Link>
        </div>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-1">
          {categories.map((c, i) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              data-testid={`category-${c.slug}`}
              className="group relative aspect-square overflow-hidden fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4">
                <div className="font-serif text-lg text-stone-50 leading-tight">{c.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED AUCTIONS */}
      {featured.length > 0 && (
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-24">
        <div className="flex items-end justify-between">
          <div>
            <div className="overline text-stone-500">{t("Selezione della settimana")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-2">{t("Lotti in evidenza")}</h2>
          </div>
          <Link to="/browse" data-testid="see-all-auctions" className="underline-hover text-sm">{t("Tutte le aste →")}</Link>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {featured.map((a, i) => <AuctionCard key={a.id} auction={a} index={i} />)}
        </div>
      </section>
      )}

      {/* METODO */}
      <section className="mt-32 bg-stone-900 text-stone-50 relative overflow-hidden grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline text-[#B49A5F]">{t("Il metodo Rarium")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-4">{t("Tre passaggi. Zero approssimazioni.")}</h2>
            <p className="mt-6 text-stone-400 leading-relaxed max-w-md">
              {t("Ogni oggetto arriva al mercato solo dopo essere passato dalle mani di un esperto della sua categoria.")}
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-8">
            <Step n="01" icon={<Search />} title={t("Consegna")} text={t("Carica foto e provenienza. Il nostro esperto risponde in 48 ore.")} />
            <Step n="02" icon={<ShieldCheck />} title={t("Verifica")} text={t("Autenticazione, stima e catalogo editoriale a cura di specialisti indipendenti.")} />
            <Step n="03" icon={<Gavel />} title={t("Asta")} text={t("Sette giorni di battaglia tra collezionisti. Trasparente, veloce, misurabile.")} />
          </div>
        </div>
      </section>

      {/* SEO — Aste online in Italia (contenuto keyword-rich per Google) */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline text-stone-500">{t("Chi siamo")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-2">{t("Aste online curate in Italia.")}</h2>
          </div>
          <div className="lg:col-span-7 text-stone-700 leading-relaxed space-y-4 text-lg">
            <p>
              {t("Rarium è una casa d'aste digitale specializzata in aste online curate per collezionisti italiani ed europei. Ogni settimana pubblichiamo nuove aste in sette categorie: aste di orologi vintage, aste d'arte e antiquariato, aste di gioielli, aste di auto classiche, aste di vini pregiati, aste di monete antiche e aste di libri rari.")}
            </p>
            <p>
              {t("A differenza delle grandi piattaforme generaliste, ogni asta Rarium è supervisionata da un curatore indipendente esperto della categoria. Autenticiamo la provenienza, verifichiamo le condizioni e scriviamo la scheda editoriale prima di pubblicare il lotto. Il risultato: aste online più trasparenti, con offerte reali di collezionisti veri.")}
            </p>
            <p>
              {t("Vuoi partecipare a un'asta? La ")}<a href="/register" className="underline-hover text-[#8C3A3A]">{t("registrazione è gratuita")}</a>{t(" e le offerte non prevedono commissioni per l'acquirente. Vuoi vendere? ")}<a href="/sell" className="underline-hover text-[#8C3A3A]">{t("Metti in asta il tuo oggetto")}</a>{t(" — trattenimento del 10% solo se il lotto viene aggiudicato.")}
            </p>
          </div>
        </div>
      </section>

      {/* FAQ (JSON-LD FAQPage schema in index.html) */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline text-stone-500">{t("Domande frequenti")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-2">{t("Tutto quello che devi sapere sulle nostre aste.")}</h2>
          </div>
          <div className="lg:col-span-8">
            <FaqItem q={t("Come funzionano le aste online su Rarium?")} a={t("Le aste durano tipicamente 7 giorni. Ogni lotto viene autenticato dai nostri esperti, presentato con foto, descrizione editoriale e stima di mercato. Gli utenti registrati possono fare offerte in tempo reale: l'offerta più alta al termine dell'asta si aggiudica il lotto.")} />
            <FaqItem q={t("Quali categorie di aste posso trovare?")} a={t("Sette categorie curate: Arte & Antiquariato, Orologi vintage, Gioielli, Auto Classiche, Vini pregiati, Monete antiche e Libri Rari. Ogni categoria è supervisionata da un esperto indipendente.")} />
            <FaqItem q={t("Quanto costa partecipare a un'asta?")} a={t("La registrazione e la partecipazione alle aste sono gratuite. L'acquirente paga solo il prezzo di aggiudicazione. Rarium trattiene una commissione del 10% dal venditore sull'importo di aggiudicazione.")} />
            <FaqItem q={t("Come vendo un oggetto tramite le aste Rarium?")} a={t("Registrati gratuitamente, carica una foto del tuo oggetto: la nostra AI genera automaticamente titolo, descrizione e stima di mercato. Dopo la verifica del curatore, il lotto viene pubblicato all'asta per 7 giorni.")} />
            <FaqItem q={t("I lotti in asta sono autenticati?")} a={t("Sì. Ogni lotto viene esaminato e autenticato da esperti indipendenti prima di essere presentato all'asta. Autenticazione, provenienza e condizioni sono documentate nella scheda del lotto.")} />
          </div>
        </div>
      </section>

      {/* TRUST + CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-24 mb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="overline text-stone-500">{t("Perché Rarium")}</div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-2">{t("Un mercato costruito per la fiducia.")}</h2>
          <ul className="mt-8 space-y-4 text-stone-700">
            <li className="border-t border-stone-200 pt-4"><span className="font-serif text-2xl mr-4">◆</span>{t("Autenticazione indipendente per ogni lotto.")}</li>
            <li className="border-t border-stone-200 pt-4"><span className="font-serif text-2xl mr-4">◆</span>{t("Pagamenti protetti tramite Stripe, escrow disponibile.")}</li>
            <li className="border-t border-stone-200 pt-4"><span className="font-serif text-2xl mr-4">◆</span>{t("Spedizioni assicurate porta a porta, tracciate in tempo reale.")}</li>
          </ul>
        </div>
        <div className="lg:col-span-5 bg-stone-100 p-10">
          <div className="overline text-[#8C3A3A]">Newsletter</div>
          <h3 className="font-serif text-3xl tracking-tighter mt-2">{t("La lista dei curatori.")}</h3>
          <p className="mt-3 text-stone-600 text-sm">{t("Un'email settimanale con i lotti più interessanti selezionati dai nostri esperti.")}</p>
          <form onSubmit={(e) => e.preventDefault()} className="mt-6 flex gap-3">
            <input data-testid="newsletter-input" placeholder={t("La tua email")} className="flex-1 border-b border-stone-400 bg-transparent py-3 px-0 focus:border-stone-900 focus:outline-none text-sm" />
            <button data-testid="newsletter-submit" className="text-xs uppercase tracking-widest font-semibold bg-stone-900 hover:bg-[#8C3A3A] text-stone-50 px-6 py-3 transition-colors">{t("Iscrivimi")}</button>
          </form>
        </div>
      </section>
    </div>
  );
}

const Stat = ({ k, v }) => (
  <div>
    <div className="font-serif text-3xl tracking-tighter">{k}</div>
    <div className="overline text-stone-500 mt-1">{v}</div>
  </div>
);
const Step = ({ n, icon, title, text }) => (
  <div className="border-t border-stone-700 pt-6">
    <div className="flex items-center gap-3 text-[#B49A5F]">
      <span className="font-mono text-xs">{n}</span>
      <span>{icon}</span>
    </div>
    <h4 className="font-serif text-2xl mt-4">{title}</h4>
    <p className="mt-2 text-stone-400 text-sm leading-relaxed">{text}</p>
  </div>
);
const FaqItem = ({ q, a }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <details
      onToggle={(e) => setOpen(e.target.open)}
      className="border-t border-stone-200 py-6 group cursor-pointer"
    >
      <summary className="flex items-start justify-between gap-4 list-none">
        <h3 className="font-serif text-2xl leading-tight text-stone-900 group-hover:text-[#8C3A3A] transition-colors">{q}</h3>
        <span className={`text-2xl leading-none text-[#8C3A3A] transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </summary>
      <p className="mt-4 text-stone-600 leading-relaxed max-w-3xl">{a}</p>
    </details>
  );
};
